import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from '@diligentcorp/ckeditor5-assets';
import { Box, FormLabel, Stack, Typography } from '@mui/material';
import React from 'react';
import { editorConfig } from './EditorConfig';

export interface CkRichContentEditorProps {
  value: string;
  onChange: (data: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  testId?: string;
  required?: boolean;
  errorMessage?: string;
}

function CkRichContentEditor({
  value,
  onChange,
  onBlur,
  disabled,
  label,
  placeholder,
  testId = 'rich-text-editor',
  required,
  errorMessage,
}: CkRichContentEditorProps): React.JSX.Element {
  const config = React.useMemo(
    () => ({
      ...editorConfig,
      placeholder: placeholder ?? '',
    }),
    [placeholder],
  );

  return (
    <Stack data-testid={testId} width='100%'>
      {label && (
        <FormLabel required={required} id={`${testId}-label`}>
          {label}
        </FormLabel>
      )}
      <Box
        sx={{
          width: '100%',
          '& .ck-editor__editable': {
            minHeight: '6.5em',
            height: '10em',
            resize: 'vertical',
            overflow: 'auto',
          },
          ...(errorMessage && {
            '& .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused)': {
              borderColor: 'error.main',
            },
          }),
        }}
      >
        <CKEditor
          config={config}
          editor={Editor}
          data={value}
          onChange={(_event, editor: Editor) => {
            onChange(editor.getData());
          }}
          onBlur={onBlur}
          disabled={disabled}
          onReady={(editor: Editor) => {
            const editable = editor.editing.view.getDomRoot();
            if (editable) {
              editable.setAttribute('data-testid', `${testId}-input`);
            }
          }}
        />
      </Box>
      {errorMessage && (
        <Typography variant='caption' color='error' sx={{ mt: 0.5 }}>
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
}

export default CkRichContentEditor;
